import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService } from '@/services/admin.service';
import {
  Plus,
  Edit,
  Trash2,
  X,
  Save,
  GripVertical,
  CheckCircle,
  AlertCircle,
  Search,
  Download,
  Upload as UploadIcon,
} from 'lucide-react';
import { confirmDelete, showDeleteSuccess, showDeleteError } from '@/utils/deleteConfirmation';
import type { DropdownConfig as DropdownConfigType } from '@/types';

export function DropdownConfig() {
  const [selectedCategory, setSelectedCategory] = useState('certifying_body');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editForm, setEditForm] = useState<Partial<DropdownConfigType>>({});
  const queryClient = useQueryClient();

  const categories = [
    {
      id: 'certifying_body',
      label: 'Certifying Bodies',
      description: 'Organizations that provide certifications',
      icon: '🏛️',
      usage: 'Exam forms',
    },
    {
      id: 'exam_status',
      label: 'Exam Status',
      description: 'Status values for exams',
      icon: '📊',
      usage: 'Exam management',
    },
    {
      id: 'language',
      label: 'Languages',
      description: 'Supported languages',
      icon: '🌍',
      usage: 'Exams, courses, materials',
    },
    {
      id: 'resource_type',
      label: 'Resource Types',
      description: 'Types of learning materials',
      icon: '📚',
      usage: 'Material upload',
    },
    {
      id: 'source_authority',
      label: 'Source Authority',
      description: 'Authority levels for materials',
      icon: '⭐',
      usage: 'Material categorization',
    },
    {
      id: 'modality',
      label: 'Course Modality',
      description: 'Delivery methods for courses',
      icon: '🎓',
      usage: 'Course forms',
    },
    {
      id: 'difficulty',
      label: 'Difficulty Levels',
      description: 'Question difficulty ratings',
      icon: '📈',
      usage: 'Question generation',
    },
    {
      id: 'cognitive_level',
      label: 'Cognitive Levels',
      description: "Bloom's taxonomy levels",
      icon: '🧠',
      usage: 'Question categorization',
    },
  ];

  const currentCategory = categories.find((c) => c.id === selectedCategory);

  const { data: configs, isLoading } = useQuery({
    queryKey: ['dropdown-config', selectedCategory],
    queryFn: async () => {
      try {
        const token = localStorage.getItem('accessToken') || '';
        return await adminService.getDropdownConfig(selectedCategory, token);
      } catch (error) {
        console.error('Failed to fetch dropdown config:', error);
        return [];
      }
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const token = localStorage.getItem('accessToken') || '';
      return await adminService.createDropdownConfig(data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dropdown-config', selectedCategory] });
      setIsAddingNew(false);
      setEditForm({});
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const token = localStorage.getItem('accessToken') || '';
      return await adminService.updateDropdownConfig(id, data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dropdown-config', selectedCategory] });
      setEditingId(null);
      setEditForm({});
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = localStorage.getItem('accessToken') || '';
      return await adminService.deleteDropdownConfig(id, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dropdown-config', selectedCategory] });
      showDeleteSuccess('Option has been deleted successfully.');
    },
    onError: (error: any) => {
      showDeleteError(error.response?.data?.error || 'Failed to delete option');
    },
  });

  const handleSaveEdit = (configId: string) => {
    updateMutation.mutate({ id: configId, data: editForm });
  };

  const handleAddNew = () => {
    createMutation.mutate({
      category: selectedCategory,
      ...editForm,
      order_index: (configs?.length || 0) + 1,
    });
  };

  const startEdit = (config: DropdownConfigType) => {
    setEditingId(config.config_id);
    setEditForm(config);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
    setIsAddingNew(false);
  };

  const filteredConfigs = configs?.filter(
    (c: DropdownConfigType) =>
      c.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dropdown Configuration</h1>
        <p className="text-sm text-gray-600 mt-1">
          Manage dropdown options that appear throughout the system
        </p>
      </div>

      {/* Category Grid */}
      <div className="grid grid-cols-4 gap-4">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => {
              setSelectedCategory(category.id);
              setIsAddingNew(false);
              setEditingId(null);
              setSearchTerm('');
            }}
            className={`card text-left transition-all hover:shadow-md ${
              selectedCategory === category.id
                ? 'ring-2 ring-primary-500 bg-primary-50'
                : 'hover:bg-gray-50'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="text-2xl mb-2">{category.icon}</div>
                <h3 className="font-semibold text-gray-900 text-sm mb-1">
                  {category.label}
                </h3>
                <p className="text-xs text-gray-500 line-clamp-2">
                  {category.description}
                </p>
              </div>
              {selectedCategory === category.id && (
                <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0" />
              )}
            </div>
            <div className="mt-3 pt-3 border-t">
              <span className="text-xs text-gray-500">
                {configs && selectedCategory === category.id
                  ? `${configs.length} option${configs.length !== 1 ? 's' : ''}`
                  : category.usage}
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Selected Category Panel */}
      {currentCategory && (
        <div className="card">
          {/* Panel Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center space-x-3">
                <span className="text-3xl">{currentCategory.icon}</span>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {currentCategory.label}
                  </h2>
                  <p className="text-sm text-gray-600">{currentCategory.description}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button className="btn-secondary flex items-center text-sm">
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
              <button className="btn-secondary flex items-center text-sm">
                <UploadIcon className="w-4 h-4 mr-2" />
                Import
              </button>
              <button
                onClick={() => {
                  setIsAddingNew(true);
                  setEditForm({
                    value: '',
                    label: '',
                    label_en: '',
                    is_active: true,
                  });
                }}
                className="btn-primary flex items-center text-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Option
              </button>
            </div>
          </div>

          {/* Search and Filter */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search options..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 input"
              />
            </div>
          </div>

          {/* Add New Form */}
          {isAddingNew && (
            <div className="mb-4 p-4 border-2 border-dashed border-primary-300 rounded-lg bg-primary-50">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">Add New Option</h3>
                <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Value (Technical) *
                  </label>
                  <input
                    type="text"
                    value={editForm.value || ''}
                    onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                    className="input text-sm"
                    placeholder="e.g., ANBIMA"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Label (Portuguese) *
                  </label>
                  <input
                    type="text"
                    value={editForm.label || ''}
                    onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                    className="input text-sm"
                    placeholder="e.g., ANBIMA"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Label (English)
                  </label>
                  <input
                    type="text"
                    value={editForm.label_en || ''}
                    onChange={(e) => setEditForm({ ...editForm, label_en: e.target.value })}
                    className="input text-sm"
                    placeholder="e.g., ANBIMA"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Status *
                  </label>
                  <select
                    value={editForm.is_active ? 'true' : 'false'}
                    onChange={(e) =>
                      setEditForm({ ...editForm, is_active: e.target.value === 'true' })
                    }
                    className="input text-sm"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center space-x-2 mt-3">
                <button
                  onClick={handleAddNew}
                  disabled={!editForm.value || !editForm.label}
                  className="btn-primary text-sm flex items-center"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Option
                </button>
                <button onClick={cancelEdit} className="btn-secondary text-sm">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Options List */}
          {isLoading ? (
            <div className="py-12 text-center text-gray-500">Loading options...</div>
          ) : (
            <div className="space-y-2">
              {filteredConfigs && filteredConfigs.length > 0 ? (
                filteredConfigs.map((config: DropdownConfigType) => (
                  <div
                    key={config.config_id}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      editingId === config.config_id
                        ? 'border-primary-300 bg-primary-50'
                        : config.is_active
                        ? 'border-gray-200 bg-white hover:border-gray-300'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    {editingId === config.config_id ? (
                      /* Edit Mode */
                      <div className="space-y-3">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">Edit Option</h4>
                          <button
                            onClick={cancelEdit}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        <div className="grid grid-cols-4 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Value
                            </label>
                            <input
                              type="text"
                              value={editForm.value || ''}
                              onChange={(e) =>
                                setEditForm({ ...editForm, value: e.target.value })
                              }
                              className="input text-sm font-mono"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Label (PT)
                            </label>
                            <input
                              type="text"
                              value={editForm.label || ''}
                              onChange={(e) =>
                                setEditForm({ ...editForm, label: e.target.value })
                              }
                              className="input text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Label (EN)
                            </label>
                            <input
                              type="text"
                              value={editForm.label_en || ''}
                              onChange={(e) =>
                                setEditForm({ ...editForm, label_en: e.target.value })
                              }
                              className="input text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Status
                            </label>
                            <select
                              value={editForm.is_active ? 'true' : 'false'}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  is_active: e.target.value === 'true',
                                })
                              }
                              className="input text-sm"
                            >
                              <option value="true">Active</option>
                              <option value="false">Inactive</option>
                            </select>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleSaveEdit(config.config_id)}
                            className="btn-primary text-sm flex items-center"
                          >
                            <Save className="w-4 h-4 mr-2" />
                            Save Changes
                          </button>
                          <button onClick={cancelEdit} className="btn-secondary text-sm">
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 flex-1">
                          <button className="cursor-move text-gray-400 hover:text-gray-600">
                            <GripVertical className="w-5 h-5" />
                          </button>
                          <div className="flex-1 grid grid-cols-4 gap-4">
                            <div>
                              <span className="text-xs text-gray-500 block mb-1">Value</span>
                              <code className="text-sm font-mono font-semibold text-gray-900">
                                {config.value}
                              </code>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 block mb-1">
                                Label (PT)
                              </span>
                              <span className="text-sm text-gray-900">{config.label}</span>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 block mb-1">
                                Label (EN)
                              </span>
                              <span className="text-sm text-gray-700">
                                {config.label_en || '-'}
                              </span>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 block mb-1">Status</span>
                              {config.is_active ? (
                                <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Active
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Inactive
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <button
                            onClick={() => startEdit(config)}
                            className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={async () => {
                              const confirmed = await confirmDelete({
                                title: 'Delete Option?',
                                itemName: config.label,
                              });
                              if (confirmed) {
                                deleteMutation.mutate(config.config_id);
                              }
                            }}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="py-12 text-center">
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500">
                    {searchTerm ? 'No options match your search' : 'No options configured yet'}
                  </p>
                  {!searchTerm && (
                    <button
                      onClick={() => {
                        setIsAddingNew(true);
                        setEditForm({
                          value: '',
                          label: '',
                          label_en: '',
                          is_active: true,
                        });
                      }}
                      className="mt-4 btn-primary inline-flex items-center"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Option
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Usage Info */}
          {configs && configs.length > 0 && (
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-gray-900 mb-1">Usage Information</p>
                  <p className="text-gray-600">
                    These options are used in: <strong>{currentCategory.usage}</strong>
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    Inactive options won't appear in dropdowns but existing data remains intact.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
